import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as classRoomService from '../services/classRoomService';
import type { 
  CreateClassRequest, 
  UpdateClassRequest, 
  ListQueryParams 
} from '../types/api';

// Classes list query
export const useClasses = (schoolId: string, params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.classes.list(schoolId, params),
    queryFn: () => classRoomService.getClasses(schoolId, params),
    select: (data) => ({
      classes: data.classes || [],
      pagination: data.pagination
    }),
    enabled: !!schoolId,
  });
};

// Single class query
export const useClass = (schoolId: string, classId: string) => {
  return useQuery({
    queryKey: queryKeys.classes.detail(schoolId, classId),
    queryFn: () => classRoomService.getClass(schoolId, classId),
    enabled: !!schoolId && !!classId,
  });
};

// Class students query
export const useClassStudents = (schoolId: string, classId: string, params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.classes.students(schoolId, classId, params),
    queryFn: () => classRoomService.getClassStudents(schoolId, classId, params),
    select: (data) => ({
      students: data.students || [],
      totalCount: data.total_count
    }),
    enabled: !!schoolId && !!classId,
  });
};

// Create class mutation
export const useCreateClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ schoolId, data }: { schoolId: string; data: CreateClassRequest }) => 
      classRoomService.createClass(schoolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.lists() 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.list(variables.schoolId) 
      });
      notifications.show({
        title: 'Success',
        message: 'Class created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create class',
        color: 'red',
      });
    },
  });
};

// Update class mutation
export const useUpdateClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ schoolId, classId, data }: { 
      schoolId: string; 
      classId: string; 
      data: UpdateClassRequest 
    }) => classRoomService.updateClass(schoolId, classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.lists() 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.detail(variables.schoolId, variables.classId) 
      });
      notifications.show({
        title: 'Success',
        message: 'Class updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update class',
        color: 'red',
      });
    },
  });
};

// Delete class mutation
export const useDeleteClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ schoolId, classId }: { schoolId: string; classId: string }) => 
      classRoomService.deleteClass(schoolId, classId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.lists() 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.list(variables.schoolId) 
      });
      notifications.show({
        title: 'Success',
        message: 'Class deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete class',
        color: 'red',
      });
    },
  });
};

// Add student to class mutation
export const useAddStudentToClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ schoolId, classId, studentId }: { 
      schoolId: string; 
      classId: string; 
      studentId: string 
    }) => classRoomService.addStudentToClass(schoolId, classId, studentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.students(variables.schoolId, variables.classId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.students.detail(variables.studentId) 
      });
      notifications.show({
        title: 'Success',
        message: 'Student added to class successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add student to class',
        color: 'red',
      });
    },
  });
};

// Remove student from class mutation
export const useRemoveStudentFromClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ schoolId, classId, studentId }: { 
      schoolId: string; 
      classId: string; 
      studentId: string 
    }) => classRoomService.removeStudentFromClass(schoolId, classId, studentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.classes.students(variables.schoolId, variables.classId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.students.detail(variables.studentId) 
      });
      notifications.show({
        title: 'Success',
        message: 'Student removed from class successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to remove student from class',
        color: 'red',
      });
    },
  });
};